import { supabase } from '@/lib/supabase'

export type TrendShadowRow = {
  symbol: string
  timeframe: string
  trend_v2: string | null
  fase_v2: string | null
  valid_structure_trend: string | null
  pivo_v2: string | null
  pivo_v2_preco: number | null
  pivo_v2_em: string | null
  trend_atual: string | null
  pivo_atual: string | null
  pivo_atual_em: string | null
  diverge: boolean
  last_candle_open: string | null
  computed_at: string | null
}

export type TrendShadowEvent = {
  id: number
  symbol: string
  timeframe: string
  candle_open: string | null
  from_trend: string | null
  to_trend: string | null
  phase: string | null
  created_at: string
}

const PAGE = 1000

// Comparação Trend Structure (sombra) x tendência atual — só o master enxerga (RLS)
export async function listTrendShadow(): Promise<TrendShadowRow[]> {
  const out: TrendShadowRow[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('v_trend_structure_shadow_compare')
      .select('symbol,timeframe,trend_v2,fase_v2,valid_structure_trend,pivo_v2,pivo_v2_preco,pivo_v2_em,trend_atual,pivo_atual,pivo_atual_em,diverge,last_candle_open,computed_at')
      .order('symbol', { ascending: true })
      .order('timeframe', { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) throw error
    out.push(...((data ?? []) as TrendShadowRow[]))
    if (!data || data.length < PAGE) break
  }
  return out
}

export type CaPivot = { pivot_time: string; pivot_type: string; classification: string; pivot_close: number }
export type CaLevel = { break_direction: string; level: number; pivots: { pivot_time?: string; pivot_type?: string; pivot_value?: number } | null }
export type ChartCandle = { open_time: string; open: number; high: number; low: number; close: number }

export type TrendPointsDetail = {
  summary: Record<string, any> | null
  caPivots: CaPivot[]
  caLevels: CaLevel[]
  candles: ChartCandle[]
}

// Pontos que definem a tendência em cada modelo, para um par moeda/tempo
export async function getTrendPointsDetail(symbol: string, timeframe: string, candleLimit = 120): Promise<TrendPointsDetail> {
  const [sum, piv, lvl, cs] = await Promise.all([
    supabase.from('v_trend_structure_shadow_compare').select('summary').eq('symbol', symbol).eq('timeframe', timeframe).maybeSingle(),
    supabase.from('market_pivots').select('pivot_time,pivot_type,classification,pivot_close')
      .eq('symbol', symbol).eq('timeframe', timeframe).in('classification', ['HH', 'HL', 'LH', 'LL'])
      .order('pivot_time', { ascending: false }).limit(40),
    supabase.from('pivot_levels').select('break_direction,level,pivots').eq('symbol', symbol).eq('timeframe', timeframe),
    supabase.from('ohlcv_candles').select('open_time,open,high,low,close')
      .eq('symbol', symbol).eq('timeframe', timeframe).order('open_time', { ascending: false }).limit(candleLimit),
  ])
  for (const r of [sum, piv, lvl, cs]) if (r.error) throw r.error
  return {
    summary: ((sum.data as any)?.summary ?? null) as Record<string, any> | null,
    caPivots: (piv.data ?? []) as CaPivot[],
    caLevels: (lvl.data ?? []) as CaLevel[],
    candles: ((cs.data ?? []) as any[]).map((c) => ({ open_time: c.open_time, open: +c.open, high: +c.high, low: +c.low, close: +c.close })).reverse(),
  }
}

export async function listTrendShadowEvents(limit = 50): Promise<TrendShadowEvent[]> {
  const { data, error } = await supabase
    .from('trend_structure_shadow_events')
    .select('id,symbol,timeframe,candle_open,from_trend,to_trend,phase,created_at')
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []) as TrendShadowEvent[]
}
